import type { ColumnDef } from '@tanstack/react-table';

import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import { Text } from '@src/design-system/text';
import type { TReminderLog } from '@src/shared/types/reminder';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { formatReminderTableDate } from '../shared/reminder-date-format';
import { ReminderChannelCell } from '../shared/reminder-channel-cell';
import { LogStatusBadge } from '../shared/log-status-badge';
import { getRecipientDisplay } from '../../get-recipient-display';

export const automatedRemindersColumns: ColumnDef<TReminderLog>[] = [
  {
    id: 'recipient',
    accessorFn: (row) => getRecipientDisplay(row).name,
    header: 'Recipient',
    cell: ({ row }) => {
      const { name, avatarUrl } = getRecipientDisplay(row.original);

      return (
        <div className='flex min-w-0 max-w-[200px] items-center gap-2'>
          <Avatar className='size-5 rounded-[4px] shrink-0'>
            <AvatarImage src={avatarUrl} />
            <AvatarFallback>{name.slice(0, 1) || '?'}</AvatarFallback>
          </Avatar>
          <Text className='truncate text-sm'>{name}</Text>
        </div>
      );
    },
  },
  {
    id: 'program',
    accessorFn: (row) => row.reminder.program?.name ?? '',
    header: 'Program',
    cell: ({ row }) => (
      <span className='line-clamp-2 max-w-[160px] text-sm text-gray-1000'>
        {row.original.reminder.program?.name ?? '—'}
      </span>
    ),
  },
  {
    id: 'channel',
    accessorKey: 'channel',
    header: 'Channel',
    enableSorting: false,
    cell: ({ row }) => <ReminderChannelCell channel={row.original.channel} />,
  },
  {
    id: 'reminderName',
    accessorFn: (row) => row.reminder.name,
    header: 'Reminder Name',
    cell: ({ row }) => (
      <span className='line-clamp-2 max-w-[220px] text-sm font-medium text-gray-1000'>
        {row.original.reminder.name}
      </span>
    ),
  },
  {
    id: 'reminderType',
    accessorFn: (row) => row.reminder.type,
    header: 'Reminder Type',
    cell: ({ row }) => (
      <span className='text-sm text-gray-1000'>
        {formatTextForDisplay(String(row.original.reminder.type))}
      </span>
    ),
  },
  {
    id: 'sentAt',
    accessorKey: 'created_at',
    header: 'Sent At',
    cell: ({ row }) => (
      <span className='whitespace-nowrap text-sm text-gray-1000'>
        {formatReminderTableDate(row.original.created_at)}
      </span>
    ),
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => <LogStatusBadge status={row.original.status} />,
  },
];
